import React from 'react';
import { InformationCardProps } from '../../InformationCard.types';
import { Typography } from '../../../typography';
import { CardList } from '../CardList';
import { Validate } from '@dewise-devex/backstage-plugin-utilities-common-client';
import { CardBadge } from '../CardBadge';
import { styledComponent } from '../../../../hoc';

/**
 * `GroupContainer` is a styled `div` that wraps the individual groups of cards.
 * It ensures consistent bottom margin between groups, except for the last group.
 */
const GroupContainer = styledComponent('div')(() => ({
  marginBottom: 24,

  '&:last-child': {
    marginBottom: 0,
  },
}));

/**
 * `CardGroup` Component
 *
 * This component groups and displays information cards based on the provided `consolidatedData`.
 * It renders each group of items either as badges or lists, based on the `asBadge` flag for each group.
 *
 * @remarks
 * - Each group is displayed with a title (`group` field).
 * - Cards can either be rendered as `CardBadge` or `CardList`, based on the `asBadge` flag.
 * - If `items` are missing or empty in any group, an error or message will be shown.
 *
 * @throws Will throw an error if `items` are not provided in a group.
 * @param {InformationCardProps} props - The properties passed to the `CardGroup` component.
 * @returns {JSX.Element} A container of grouped information cards, rendered as either badges or lists.
 *
 * @example
 * ```tsx
 * import CardGroup from './CardGroup';
 *
 * const data = {
 *   consolidatedData: [
 *     {
 *       group: 'Group 1',
 *       items: [
 *         { primary: 'Item 1', secondary: 'Description 1' },
 *         { primary: 'Item 2', secondary: 'Description 2' },
 *       ],
 *       asBadge: true,
 *     },
 *     {
 *       group: 'Group 2',
 *       items: [
 *         { primary: 'Item A', secondary: 'Description A' },
 *         { primary: 'Item B', secondary: 'Description B' },
 *       ],
 *       asBadge: false,
 *     },
 *   ],
 * };
 *
 * export const App = () => <CardGroup consolidatedData={data.consolidatedData} />;
 * ```
 *
 * @category InformationCard
 * @document CardGroupComponentUsage.md
 */
export const CardGroup = (props: InformationCardProps): JSX.Element => {
  const items = props.consolidatedData || [];

  return (
    <>
      {items.map(item => {
        // Check if the group has items, if not, throw an error
        if (!item.items) {
          throw new Error('InformationGroup component must have items');
        }

        return (
          <GroupContainer key={`${item.group}`}>
            {/* Display group title */}
            <Typography fontSize={14} fontWeight="medium" gutterBottom>
              {item.group}
            </Typography>

            {/* If items are empty, show a message */}
            {Validate.isEmptyArray(item.items) ? (
              <Typography>No items to display</Typography>
            ) : (
              item.items.map((data, index) => {
                // Prepare the data for each individual item in the group, ensuring it is in 'list' format
                const groupInformation: InformationCardProps = {
                  as: 'list',
                  title: '',
                  data: {
                    ...item,
                    items: [data],
                  },
                };

                // Conditionally select which component to use based on `asBadge` flag
                const Component = item.asBadge ? CardBadge : CardList;

                return (
                  <Component
                    {...groupInformation}
                    key={`${item.group}-${index}_badge_root`}
                  />
                );
              })
            )}
          </GroupContainer>
        );
      })}
    </>
  );
};
